<template>
  <div v-if="event" class="location" :class="{ 'reduced': reduced, 'fullreduce': fullreduce }">
    <div class="top-left z1 mt2 ml2 sm-mt3 sm-ml3"><router-link to="/">Back</router-link></div>
    <div class="top-right right-align z1 xs-hide mt2 mr2 sm-mt3 sm-mr3" v-if="dataLoaded" style="max-width: 80vw">
      <div><span class="truncate inline-block" v-html="eventSummary"></span></div>
      <div><a href="#event-details" v-scroll-to="{ el: '#event-details', duration: 200 }">↳ Event Details</a></div>
    </div>
    <div class="top-right right-align z1 sm-hide md-hide lg-hide mt2 mr2 sm-mt3 sm-mr3" v-if="dataLoaded">
      <a href="#event-details" v-scroll-to="{ el: '#event-details', duration: 200, offset: -50 }">↳ Event Details</a>
    </div>
    <div class="bottom-left z1 xs-hide mb2 ml2 sm-mb3 sm-ml3">
      <a href="#" v-scroll-to="{
        el: 'body',
        duration: 200
      }">Text,</a>&nbsp;<a href="#images" v-scroll-to="{
        el: '#event-images',
        duration: 200
      }">Images</a></div>
    <div class="bottom-right right-align z1 xs-hide mb2 mr2 sm-mb3 sm-mr3">
      {{ processDate(event.data.date) }}<span class="ml1">{{ getText(event.data.times) }}</span>
    </div>

    <div class="backing"></div>

    <div class="overlay-wrapper z2 screen">
      <img class="overlay screen" :src="overlay" alt="water overlay" />
    </div>
    <div class="coastline-wrapper z3">
      <img class="coastline z2" :src="border" alt="coastline overlay" />
    </div>

    <div v-if="text" class="text-title col-12 md-col-10 mx-auto center relative z1">
      <div class="sm-mx3">
        <span v-waypoint="{ active: true, callback: onWaypoint, options: intersectionOptions }" v-text='`"${getText(text.data.title)}"`'></span><br/>
        <span v-text='`by ${getText(text.data.author)}`'></span><a target="_blank" v-if="showFullText && event.data.text_pdf.url" :href="event.data.text_pdf.url" class="about-link">(Download)</a>
      </div>
    </div>

    <div v-else class="loading text-title col-12 md-col-10 mx-auto center relative z1">
      <div class="sm-mx3">
        Loading...
      </div>
    </div>

    <div v-if="text" class="col-11 md-col-8 lg-col-6 mx-auto relative z1">
      <div class="text-content serif" v-if="text && showFullText" v-html="getHtml(text.data.text)"></div>
      <div class="text-content serif" v-else-if="text">
        <span class="block shortened relative" v-html="shortText(getText(text.data.text))"></span>
        <div class="sans coming-soon">Full text will be released on day of event</div>
      </div>
      <div id="about-text" class="author-content fs-xs" v-html="getHtml(text.data.about)"></div>

      <div class="mt3 fs-xs">
        <span class="mr1"><a v-if="text.data.about.length" href="https://prismic-io.s3.amazonaws.com/ftiam/e55cf3e4-8d0b-4b9c-8428-efe3202ca965_FTIAM-Anthology-v2-spread-lines.pdf">Read the Anthology</a></span>
        <a v-if="text.data.about.length" href="#" v-scroll-to="{
          el: 'body',
          duration: 200
        }">Back to Top</a>
      </div>
    </div>

    <event-details id="event-details" :event="event"></event-details>

    <event-images
      id="event-images"
      v-waypoint="{ active: true, callback: onImageWaypoint, options: imageWaypointOptions }"
      :event="event"></event-images>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getText, getHtml, processDate } from '@/utils'
import EventDetails from '@/components/EventDetails'
import EventImages from '@/components/EventImages'
import Prismic from 'prismic-javascript'
import dayjs from 'dayjs'

export default {

  name: 'Event',

  data () {
    return {
      reduced: false,
      fullreduce: false,
      text: '',
      intersectionOptions: {
        root: null,
        rootMargin: '-20% 0px 0px 0px',
        threshold: [0]
      },
      imageWaypointOptions: {
        root: null,
        rootMargin: '-20% 0px 0px 0px',
        threshold: [0]
      }
    }
  },

  metaInfo () {
    if (this.title) {
      return {
        title: this.getText(this.title),
        meta: [
          { name: 'description', content: this.description }
        ]
      }
    }
  },

  components: {
    EventDetails,
    EventImages
  },

  mounted () {
    this.loadText()
  },

  watch: {
    events () {
      this.loadText()
    }
  },

  computed: {
    ...mapState([
      'events',
      'weather',
      'apiEndpoint'
    ]),
    border () {
      return this.event.data.coastline_border.url
    },
    overlay () {
      return this.event.data.coastline.url
    },
    dataLoaded () {
      return this.events.length && this.weather.length
    },
    event () {
      return this.events.find(x => x.slugs[0] === this.$route.params.slug)
    },
    eventSummary () {
      if (this.weather.length) {
        let weather = this.weather.find(x => x.id === this.event.id).weather
        return `<span>${this.getText(this.event.data.title)} (${this.getText(this.event.data.beach_title)})</span><span class="ml2">${Math.round(weather.currently.temperature)}° ${weather.currently.summary}</span>`
      } else {
        return `<span>${this.getText(this.event.data.title)} (${this.getText(this.event.data.beach_title)})</span>`
      }
    },
    showFullText () {
      return dayjs().isSame(dayjs(this.event.data.date)) || dayjs().isAfter(dayjs(this.event.data.date))
    },
    title () {
      if (this.event) {
        return this.event.data.title
      } else {
        return null
      }
    },
    description () {
      if (this.event) {
        return `${this.processDate(this.event.data.date)} ${this.getText(this.event.data.times)}, ${this.getText(this.event.data.title)} (${this.getText(this.event.data.beach_title)}). Meeting details: ${this.getText(this.event.data.meeting_point)}`
      }
    }
  },

  methods: {
    getHtml,
    getText,
    processDate,
    loadText () {
      if (this.text === '') {
        let event = this.events.find(x => x.slugs[0] === this.$route.params.slug)
        if (event) {
          Prismic.getApi(this.apiEndpoint).then((api) => {
            api.getByID(event.data.text.id).then((document) => {
              this.text = document
            })
          })
        }
      }
    },
    onWaypoint ({ direction }) {
      if (direction === this.$waypointMap.DIRECTION_TOP) {
        this.reduced = true
      } else if (direction === this.$waypointMap.DIRECTION_BOTTOM) {
        this.reduced = false
      }
    },
    onImageWaypoint ({ direction }) {
      if (direction === this.$waypointMap.DIRECTION_TOP) {
        this.fullreduce = true
      } else if (direction === this.$waypointMap.DIRECTION_BOTTOM) {
        this.fullreduce = false
      }
    },
    shortText(text) {
      return text.substr(0,500) + ' &hellip;'
    }
  }
}
</script>

<style lang="css" scoped>
  .backing {
    z-index: 0;
    background: white;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .truncate {
    width: 100%;
    transition: all 0.2s ease-out;
  }

  .overlay-wrapper, .coastline-wrapper {
    opacity: 1;
    pointer-events: none;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    img {
      transition: opacity 3s ease-out;
      height: 100vh;
      width: 100vw;
      position: absolute;
      display: block;
      object-fit: cover;
    }
  }

  .screen {
    mix-blend-mode: screen;
  }

  .text-title {
    font-size: var(--fs-responsive-xl);
    line-height: 1.2;
    margin-top: 40vh;
  }

  .loading {
    margin-bottom: 50vh;
  }

  .coming-soon {
    border: 1px solid black;
    border-radius: 36px;
    text-align: center;
    padding: 1em;
    margin-top: 3em;
  }

  .about-link {
    font-size: 0.25em;
    margin-top: 0.5em;
    margin-left: 0.75em;
    position: absolute;
    vertical-align: super;
  }

  .shortened {
    /*&:before {
      content:'';
      width:100%;
      height:100%;
      position:absolute;
      left:0;
      top:0;
      background:linear-gradient(transparent 120px, white);
    }*/
  }

  @media only screen and (max-width: 40em) {
    .about-link {
      font-size: 0.4em;
    }
    /*.shortened:before {
      display: none;
    }*/
  }

  .text-content {
    margin-top: 29vh;
    margin-bottom: 3em;
  }

  .reduced {
    .coastline {
      opacity: 0.15;
    }
/*    .overlay {
      opacity: 0.25;
    }*/
    .truncate {
      width: 200px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:hover {
        width: 100%;
      }
    }
  }

  .fullreduce {
    .coastline, .overlay {
      opacity: 0;
    }
  }
</style>
